import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useCreateBackofficeCompanyFromWebsiteMutation } from "modules/backoffice/hooks/companies/useCreateBackofficeCompanyFromWebsiteMutation"
import { useImpersonateOrganizationMutation } from "modules/backoffice/hooks/organizations/useImpersonateOrganizationMutation"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateBackofficeCompanyFromWebsiteBodySchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

export const BackofficeCompanyCreatorFromWebsite = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof CreateBackofficeCompanyFromWebsiteBodySchema>>({
    resolver: zodResolver(CreateBackofficeCompanyFromWebsiteBodySchema),
    defaultValues: {
      company: { locale: "en_US" },
    },
  })
  const { mutateAsync } = useCreateBackofficeCompanyFromWebsiteMutation()
  const { mutateAsync: impersonateOrganizationMutation } = useImpersonateOrganizationMutation()

  const onSubmit = async (data: z.infer<typeof CreateBackofficeCompanyFromWebsiteBodySchema>) => {
    const result = await mutateAsync(data)
    toast.success(t("backoffice.companies.websiteCreator.createSuccess"))

    await impersonateOrganizationMutation(result.data.organization.id)
  }

  return (
    <div className="flex flex-col gap-6">
      <Section
        title={t("backoffice.companies.websiteCreator.title")}
        description={t("backoffice.companies.websiteCreator.description")}
      />
      <Form
        {...form}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
        errorTranslations={getErrorTranslations()}
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="company.name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("backoffice.companies.websiteCreator.fields.name")}</FormLabel>
                <FormControl>
                  <Input {...field} required />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company.locale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("backoffice.companies.websiteCreator.fields.locale")}</FormLabel>
                <FormControl>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={(value) => {
                      field.onChange(value === "" ? undefined : value)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("backoffice.companies.websiteCreator.fields.locale")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en_US">
                        {t(
                          "backoffice.companies.companyCreator.section.company.fields.localeOptions.en_US",
                        )}
                      </SelectItem>
                      <SelectItem value="es_ES">
                        {t(
                          "backoffice.companies.companyCreator.section.company.fields.localeOptions.es_ES",
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company.website"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t("backoffice.companies.websiteCreator.fields.website")}</FormLabel>
                <FormControl>
                  <Input {...field} type="url" placeholder="https://example.com" required />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex justify-end">
          <Button disabled={!form.formState.isValid || form.formState.isSubmitting}>
            {t("backoffice.companies.websiteCreator.submit")}
          </Button>
        </div>
      </Form>
    </div>
  )
}
